import { h, Fragment } from 'preact'
import { UserBubble } from './UserBubble.jsx'
import { AiBubble } from './AiBubble.jsx'
import { ThinkingBubble } from './ThinkingBubble.jsx'
import { SubagentPanel } from './SubagentPanel.jsx'
import { StatusAlert } from './StatusAlert.jsx'
import { CostBadge } from './CostBadge.jsx'

/**
 * ChatThread — top-level component that renders the full message list.
 *
 * Reads signals from store; Preact re-renders only changed components
 * because signals short-circuit the VDOM diff.
 *
 * Scroll-to-bottom is handled by the Stimulus ai_controller (MutationObserver
 * on the outputTarget/.chat-messages container), not here.
 *
 * Props: { store, formatter }
 * store: chatStore instance (from chat-store.js)
 * formatter: ChatFormatter instance
 */
export function ChatThread({ store, formatter }) {
  const messages            = store.messages.value
  const streamingId         = store.streamingId.value
  const streamingHasContent = store.streamingHasContent.value
  const reasoningMap        = store.reasoningMap.value
  const buffer              = store.streamBufferSignal.value
  const status              = store.status.value
  const subagentsMap        = store.subagents.value
  const i18n                = store.i18n

  // Subagent panels sorted by first-seen time — stable insertion order even if
  // JS object key order were ever non-deterministic.
  const subagentList = Object.values(subagentsMap).sort((a, b) => a.firstSeen - b.firstSeen)

  // Show standalone StatusAlert when:
  // - error state, OR
  // - no content has appeared yet in this streaming cycle (connecting phase)
  // Once streamingHasContent=true the bar never shows again (even mid-stream tool calls).
  const isError      = status?.code === 'error'
  const isConnecting = status && !isError && !streamingHasContent

  // Last completed AI message (for cost badge — shown after subagents)
  const lastAiMsg = [...messages].reverse().find(m => m.type === 'ai' && !m.isStreaming)

  // The currently streaming message is rendered AFTER subagent panels
  // so subagents always appear above the AI response bubble.
  const streamingMsg = streamingId ? messages.find(m => m.id === streamingId) : null

  function renderAiMsg(msg) {
    const isStreaming = msg.id === streamingId
    const reasoning   = reasoningMap[msg.id]

    if (isStreaming && !streamingHasContent) return null

    return (
      <Fragment key={msg.id}>
        {reasoning && (
          <ThinkingBubble
            msgId={msg.id}
            reasoningText={reasoning}
            i18n={i18n}
          />
        )}
        <AiBubble
          message={msg}
          store={store}
          formatter={formatter}
          isStreaming={isStreaming}
          buffer={isStreaming ? buffer : null}
          status={isStreaming ? status : null}
        />
      </Fragment>
    )
  }

  return (
    // Fragment: children are direct children of .chat-messages (the outputTarget).
    // Scroll handling is owned by the Stimulus controller via MutationObserver.
    <>
      {/* All messages except the currently streaming one */}
      {messages.map(msg => {
        if (msg.type === 'user') {
          return <UserBubble key={msg.id} message={msg} i18n={i18n} />
        }
        if (msg.type === 'ai') {
          if (msg.id === streamingId) return null  // rendered below, after subagents
          return renderAiMsg(msg)
        }
        return null
      })}

      {/* Subagent panels appear above the streaming AI response — one panel
          per parallel run (instanceId), so concurrent runs of the same
          subagent stay separate cards. */}
      {subagentList.map(agent => (
        // ps-5 matches the AI bubble content indent (avatar ~1.75rem + me-3 1rem ≈ 3rem)
        <div key={agent.instanceId || agent.name} class="ps-5 mb-1">
          <SubagentPanel agent={agent} i18n={i18n} formatter={formatter} />
        </div>
      ))}

      {/* Streaming AI message rendered after subagents */}
      {streamingMsg && renderAiMsg(streamingMsg)}

      {/* Cost badge after all subagent panels — indented to match AI bubble content */}
      {lastAiMsg?.cost && !streamingId && (
        <div class="ai-cost-row ps-5 mt-1 mb-3">
          <CostBadge cost={lastAiMsg.cost} i18n={i18n} />
        </div>
      )}

      {(isError || isConnecting) && (
        <StatusAlert status={status} i18n={i18n} />
      )}
    </>
  )
}
